import {
  AppointmentsApi,
  AppointmentType,
  AppointmentTypeList,
  ErrorResponse,
  FindAppointmentTypesRequest,
} from '@citruscamps/citrus-client'
import { DateTime } from 'luxon'
import { Dispatch } from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from 'react-query'
import {
  DefaultDetailsPagination,
  DefaultMainPagination,
} from '../../../../../constants/pagination'
import {
  IFetchManyProps,
  NonQueryAttributes,
  useFetchInfiniteMany,
  useFetchMany,
} from '../../../../../hooks/useFetchMany'
import { Pagination } from '../../../../../interfaces/pagination'
import { generateApiConfig } from '../../../../../utils/client-config'
import { useUpdateAppointmentTypes } from '../../../hooks/useUpdateAppointmentTypes'
import { FilterDateRange } from './filter-range'

export const DefaultAppointmentTypeFetchProps: AppointmentTypeFetchProps = {
  sort: 'created',
  order: 'ASC',
  query: {},
}

export type ScheduledAttributes = `scheduled_${keyof FilterDateRange}`
export interface AppointmentTypeFilterProps
  extends Omit<FindAppointmentTypesRequest, NonQueryAttributes | ScheduledAttributes> {
  scheduled?: FilterDateRange
}

export interface AppointmentTypeFetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  query: AppointmentTypeFilterProps
}

type IProps = Pick<
  IFetchManyProps<AppointmentType, AppointmentTypeFilterProps>,
  'programId' | 'fetchProps' | 'pagination' | 'setFetchProps' | 'isEnabled'
>

interface FetchAppointmentTypes {
  data: AppointmentType[]
  fetchProps: AppointmentTypeFetchProps
  error?: ErrorResponse
  isLoading: boolean
  isError: boolean
  pagination: Pagination
  search: string
  handlePublish: (appointmentType: AppointmentType) => Promise<void>
  handleUnpublish: (appointmentType: AppointmentType) => Promise<void>
  handleDelete: (appointmentType: AppointmentType) => Promise<void>
  setFetchProps: (value?: AppointmentTypeFetchProps, pagination?: Pagination) => void
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
}

export const useFetchAppointmentTypes = (props: IProps): FetchAppointmentTypes => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setPagination,
    setSearch,
    setFetchProps,
  } = useFetchMany<AppointmentType, AppointmentTypeFilterProps>({
    type: 'appointment_type',
    programId: props.programId,
    fetchProps: props.fetchProps,
    setFetchProps: props.setFetchProps,
    defaultFetchProps: DefaultAppointmentTypeFetchProps,
    defaultPagination: props.pagination || DefaultMainPagination,
    isEnabled: !!props.programId && !!props.isEnabled,
    queryFn: async (queryParams): Promise<AppointmentTypeList> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new AppointmentsApi(generateApiConfig())
      return await client.findAppointmentTypes(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search,
          id: query?.id && query?.id.length > 0 ? query.id : undefined,
          exclude_id:
            query?.exclude_id && query?.exclude_id.length > 0 ? query.exclude_id : undefined,
          type: query?.type && query?.type.length > 0 ? query.type : undefined,
          status: query?.status && query?.status.length > 0 ? query.status : undefined,
          scheduled_lt: query?.scheduled?.lt
            ? typeof query.scheduled.lt === 'string'
              ? DateTime.fromISO(query.scheduled.lt).toJSDate()
              : query.scheduled.lt
            : undefined,
          scheduled_gt: query?.scheduled?.gt
            ? typeof query.scheduled.gt === 'string'
              ? DateTime.fromISO(query.scheduled.gt).toJSDate()
              : query.scheduled.gt
            : undefined,
          scheduled_lte: query?.scheduled?.lte
            ? typeof query.scheduled.lte === 'string'
              ? DateTime.fromISO(query.scheduled.lte).toJSDate()
              : query.scheduled.lte
            : undefined,
          scheduled_gte: query?.scheduled?.gte
            ? typeof query.scheduled.gte === 'string'
              ? DateTime.fromISO(query.scheduled.gte).toJSDate()
              : query.scheduled.gte
            : undefined,
        },
        { signal },
      )
    },
  })
  const { handlePublish, handleUnpublish, handleDelete } = useUpdateAppointmentTypes({
    programId: props.programId,
  })

  return {
    data,
    fetchProps,
    error,
    isError,
    isLoading,
    pagination,
    search: _search,
    setFetchProps,
    setSearch,
    setPagination,
    handlePublish,
    handleUnpublish,
    handleDelete,
  }
}

interface FetchInfiniteAppointmentType
  extends Omit<FetchAppointmentTypes, 'setFetchProps' | 'setPagination'> {
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<AppointmentTypeList, ErrorResponse>>
}

interface IInfiniteProps extends IProps {
  fetchAll?: boolean
}

export const useFetchInfiniteAppointmentTypes = (
  props: IInfiniteProps,
): FetchInfiniteAppointmentType => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setSearch,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useFetchInfiniteMany<AppointmentType, AppointmentTypeFilterProps>({
    type: 'appointment_type',
    programId: props.programId,
    fetchProps: props.fetchProps || DefaultAppointmentTypeFetchProps,
    pagination: props.pagination || DefaultDetailsPagination,
    fetchAll: props.fetchAll,
    queryFn: async (queryParams): Promise<AppointmentTypeList> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new AppointmentsApi(generateApiConfig())
      return await client.findAppointmentTypes(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search,
          id: query?.id && query?.id.length > 0 ? query.id : undefined,
          exclude_id:
            query?.exclude_id && query?.exclude_id.length > 0 ? query.exclude_id : undefined,
          type: query?.type && query?.type.length > 0 ? query.type : undefined,
          status: query?.status && query?.status.length > 0 ? query.status : undefined,
          scheduled_lt: query?.scheduled?.lt
            ? typeof query.scheduled.lt === 'string'
              ? DateTime.fromISO(query.scheduled.lt).toJSDate()
              : query.scheduled.lt
            : undefined,
          scheduled_gt: query?.scheduled?.gt
            ? typeof query.scheduled.gt === 'string'
              ? DateTime.fromISO(query.scheduled.gt).toJSDate()
              : query.scheduled.gt
            : undefined,
          scheduled_lte: query?.scheduled?.lte
            ? typeof query.scheduled.lte === 'string'
              ? DateTime.fromISO(query.scheduled.lte).toJSDate()
              : query.scheduled.lte
            : undefined,
          scheduled_gte: query?.scheduled?.gte
            ? typeof query.scheduled.gte === 'string'
              ? DateTime.fromISO(query.scheduled.gte).toJSDate()
              : query.scheduled.gte
            : undefined,
        },
        { signal },
      )
    },
  })
  const { handlePublish, handleUnpublish, handleDelete } = useUpdateAppointmentTypes({
    programId: props.programId,
  })
  return {
    data,
    error,
    fetchProps,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    pagination,
    search: _search,
    fetchNextPage,
    handlePublish,
    handleUnpublish,
    handleDelete,
    setSearch,
  }
}
